import React, { ReactElement } from 'react'
import { useAppSelector } from 'store/hooks'
import { Flex } from 'theme-ui'
import { Button, Svg } from 'ui'
import BackIcon from 'images/back-icon-big.svg'
import { useEditOrganizationMutation } from 'store/api/organization'
import OrganizationsAccordion from './OrganizationAccordion'
import NavMenuItem from 'components/Dashboard/NavBar/NavMenuItem'
import MyOrganizationIcon from 'images/organization/my-organization-icon.svg'
import { OrganizationNavRelativePath } from './OrganizationNavBarContent'
import ApiIcon from 'images/dashboard/navBar/api-icon.svg'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import customUnescape from 'utils/customUnescape'
import useGetOrganization from 'components/Dashboard/utils/useGetOrganization'
import { navigate } from '@reach/router'
import useClearImpersonationState from 'components/Dashboard/utils/useClearImpersonationState'

export default function ImpersonationNavMenu(): ReactElement {
  const { isFetching, data } = useGetOrganization()

  const subOrgBeingViewed = data?.organization

  return (
    <Flex
      sx={{
        width: '100%',
        gap: '0.8rem',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <ParentOrgBackButton />
      <OrganizationsAccordion
        name={customUnescape(subOrgBeingViewed?.name)}
        isFetching={isFetching}
        isSubOrg
      >
        <Flex
          sx={{
            width: '100%',
            flexDirection: 'column',
            gap: '0.1rem',
          }}
        >
          <NavMenuItem
            activeIcon={MyOrganizationIcon}
            relativePath={OrganizationNavRelativePath.MY_ORGANIZATION}
            label="My Organization"
          />
          <NavMenuItem
            activeIcon={ApiIcon}
            relativePath={OrganizationNavRelativePath.API}
            label="API"
          />
        </Flex>
      </OrganizationsAccordion>
    </Flex>
  )
}

const ParentOrgBackButton = () => {
  const isTablet = useBreakpointIndex() === 1
  const isNavBarExpanded = useAppSelector(s => s.dashboard.isNavBarExpanded)
  const editOrganizationMutation = useEditOrganizationMutation({
    fixedCacheKey: 'editOrganizationRegion',
  })
  const isDisabled = editOrganizationMutation[1].isLoading
  const clearImpersonationState = useClearImpersonationState()

  return (
    <Button
      variant="secondaryWithoutScaling"
      data-testid="impersonation-back-button"
      ariaLabel="impersonation back button"
      disabled={isDisabled}
      sx={{
        width: '100%',
        height: '3.8rem',
        py: 0,
        px: '2.1rem',
        borderRadius: ['2rem', '2rem', '4.5rem'],
        gap: '0.8rem',
        fontSize: '1.5rem',
        fontWeight: '700',
        justifyContent: 'center',
        boxShadow:
          '0px -1px 0px 0px rgba(0, 0, 0, 0.25) inset, 0px 1px 1px 0px rgba(0, 0, 0, 0.20), 0px 1px 0px 0px rgba(255, 255, 255, 0.20) inset',
        backgroundColor: 'exitOrgButton',
      }}
      onClick={() => {
        clearImpersonationState()
        navigate('/dashboard/sub-orgs')
      }}
    >
      <Svg
        svg={BackIcon}
        fill={isDisabled ? 'white50' : 'white'}
        sx={{
          width: '1.6rem',
          height: '1.6rem',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        descriptiveText="Back arrow to exit organization"
      />
      <Flex
        sx={{
          display: isNavBarExpanded && !isTablet ? 'flex' : 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        Exit Organization
      </Flex>
    </Button>
  )
}
